import { useState } from "react";

import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import type { OnsiteUpsellProductSelection } from "@intelligems/ig-types/schemas/visual-builder/onsite-upsell-rules";
import { MenuItem } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useDispatch } from "@src/store";
import { openOnsiteUpsellActionProductPicker } from "@src/store/slices/productPicker/thunks/onsiteUpsell/openOnsiteUpsellActionProductPicker";
import { EntitySelect } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/EntitySelect";
import { EntityOption } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/entityOptions";
import { PickerButton } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/PickerButton";
import { SelectedProductNames } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/SelectedProductNames";
import {
  createProductSource,
  MEMBERSHIP_OPTIONS,
  PRODUCT_SOURCE_OPTIONS,
} from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/options";

// THEN SHOW: one product source per rule (recommendations / specific products /
// collection / tag). Specific products open the variant product picker.
export const ProductSourceSelect = ({
  products,
  ruleIndex,
  collections,
  tags,
  onChange,
}: {
  products: OnsiteUpsellProductSelection;
  ruleIndex: number;
  collections: EntityOption[];
  tags: EntityOption[];
  onChange: (next: OnsiteUpsellProductSelection) => void;
}) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  return (
    <Stack spacing={1}>
      <IgSelect<OnsiteUpsellProductSelection["source"]>
        fullWidth
        size="small"
        value={products.source}
        onChange={(e) => onChange(createProductSource(e.target.value))}
      >
        {PRODUCT_SOURCE_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </IgSelect>

      {products.source === "products" && (
        <>
          <PickerButton
            count={products.products.length}
            expanded={expanded}
            onEdit={() =>
              void dispatch(openOnsiteUpsellActionProductPicker(ruleIndex))
            }
            onToggle={() => setExpanded((open) => !open)}
          />
          {expanded && products.products.length > 0 && (
            <SelectedProductNames
              productIds={products.products.map((p) => p.productId)}
              knownTitles={Object.fromEntries(
                products.products.map((p) => [p.productId, p.productTitle])
              )}
            />
          )}
        </>
      )}
      {/* Including / Excluding, then which collection or tag. */}
      {(products.source === "collection" || products.source === "tag") && (
        <Stack direction="row" spacing={1}>
          <IgSelect<string>
            size="small"
            value={products.include ? "true" : "false"}
            onChange={(e) =>
              onChange({ ...products, include: e.target.value === "true" })
            }
            sx={{ minWidth: 120 }}
          >
            {MEMBERSHIP_OPTIONS.thenShow.map((o) => (
              <MenuItem key={String(o.value)} value={String(o.value)}>
                {o.label}
              </MenuItem>
            ))}
          </IgSelect>
          {products.source === "collection" ? (
            <EntitySelect
              options={collections}
              value={products.collectionId}
              onChange={(collectionId) =>
                onChange({ ...products, collectionId })
              }
              placeholder="Search collections"
            />
          ) : (
            <EntitySelect
              options={tags}
              value={products.tag}
              onChange={(tag) => onChange({ ...products, tag })}
              placeholder="Search tags"
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
